import type {
  OpencodeMessage,
  OpencodeMessagePart,
} from "schemas/generated/opencode";

import React from "react";

import { ChatMessageListContext } from "@agent-ui/features/chat/components/Chat";
import { OpencodeAssistantGroupItem } from "@agent-ui/features/chat/components/chat-elements/chat-message/OpencodeMessageItem";
import UserMessage from "@agent-ui/features/chat/components/chat-elements/message-list/UserMessage";
import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import {
  useIsSessionStreaming,
  useSubagentSessions,
} from "@agent-ui/features/chat/store/SessionStoreContext";
import { getSandboxStatusDisplayText } from "@agent-ui/features/chat/utils/sandboxStatus";
import { useOptionalReploAgent } from "@v2/contexts/ReploAgentContext";
import { useRequiredContext } from "replo-utils/react/context";

function isVisibleMessagePart(part: OpencodeMessagePart): boolean {
  return (
    (part.partData.type === "text" && part.partData.text.trim().length > 0) ||
    part.partData.type === "file"
  );
}

/**
 * Detects whether a message was aborted/cancelled by checking the error field for the MessageAbortedError
 * name. This MessageAbortedError is set by opencode in the message
 *
 * @author Max 2026-02-27
 */
function isMessageCancelled({
  message,
}: {
  message: OpencodeMessage;
}): boolean {
  const { error } = message;

  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name: string }).name === "MessageAbortedError"
  );
}

export function MessageList() {
  const sessionUuid = useSessionUuid();

  const isChatLoading = useIsSessionStreaming(sessionUuid);
  const agentContext = useOptionalReploAgent();
  const sandboxStatus = sessionUuid
    ? agentContext?.state.streamingSessionState?.[sessionUuid]?.sandboxStatus
    : undefined;
  const statusText = sandboxStatus
    ? getSandboxStatusDisplayText(sandboxStatus)
    : null;
  const { messages } = useRequiredContext(ChatMessageListContext);
  const subagentSessions = useSubagentSessions(sessionUuid);
  const subagentSessionIdSet = React.useMemo(
    () => new Set(subagentSessions.map((s) => s.agentSessionId)),
    [subagentSessions],
  );

  const subagentMessagesList = React.useMemo(() => {
    return messages.filter((m) => {
      return subagentSessionIdSet.has(m.message.opencodeSessionId);
    });
  }, [messages, subagentSessionIdSet]);

  const groupedRenderItems = React.useMemo(() => {
    const renderItems: (
      | {
          kind: "user";
          sessionMessage: (typeof messages)[number];
        }
      | {
          kind: "assistant-group";
          sessionMessages: (typeof messages)[number][];
          subagentMessages: (typeof messages)[number][];
        }
    )[] = [];

    let currentIndex = 0;
    while (currentIndex < messages.length) {
      const currentMessage = messages[currentIndex];
      if (!currentMessage) {
        currentIndex += 1;
        continue;
      }

      if (subagentSessionIdSet.has(currentMessage.message.opencodeSessionId)) {
        currentIndex += 1;
        continue;
      }

      if (currentMessage.message.role === "user") {
        const hasVisibleContent =
          currentMessage.parts.some(isVisibleMessagePart);
        if (hasVisibleContent) {
          renderItems.push({ kind: "user", sessionMessage: currentMessage });
        }
        currentIndex += 1;
        continue;
      }

      const groupedAssistantMessages: (typeof messages)[number][] = [
        currentMessage,
      ];
      currentIndex += 1;

      while (currentIndex < messages.length) {
        const nextMessage = messages[currentIndex];
        if (!nextMessage) {
          break;
        }
        if (subagentSessionIdSet.has(nextMessage.message.opencodeSessionId)) {
          currentIndex += 1;
          continue;
        }
        // Skip user messages with no visible content (e.g. plugin-injected subtask
        // prompts) so they don't break the assistant group.
        if (nextMessage.message.role === "user") {
          const hasVisibleContent =
            nextMessage.parts.some(isVisibleMessagePart);
          if (!hasVisibleContent) {
            currentIndex += 1;
            continue;
          }
          break;
        }
        if (nextMessage.message.role !== "assistant") {
          break;
        }
        groupedAssistantMessages.push(nextMessage);
        currentIndex += 1;
      }

      renderItems.push({
        kind: "assistant-group",
        sessionMessages: groupedAssistantMessages,
        subagentMessages: subagentMessagesList,
      });
    }

    return renderItems;
  }, [messages, subagentSessionIdSet, subagentMessagesList]);

  const lastAssistantGroupIndex = groupedRenderItems.reduce(
    (lastIndex, item, itemIndex) => {
      return item.kind === "assistant-group" ? itemIndex : lastIndex;
    },
    -1,
  );

  return (
    <div className="space-y-0 flex flex-col gap-3 w-full max-w-[880px] mx-auto min-h-0">
      {groupedRenderItems.map((item, itemIndex) => {
        if (item.kind === "user") {
          return (
            <UserMessage
              key={item.sessionMessage.message.id}
              messageWithParts={item.sessionMessage}
            />
          );
        }

        const firstMessageWithParts = item.sessionMessages[0];
        const lastMessageWithParts =
          item.sessionMessages[item.sessionMessages.length - 1];

        if (!firstMessageWithParts || !lastMessageWithParts) {
          return null;
        }

        const isCancelled = isMessageCancelled({
          message: lastMessageWithParts.message,
        });

        return (
          <OpencodeAssistantGroupItem
            key={`assistant-group-${firstMessageWithParts.message.id}`}
            sessionMessages={item.sessionMessages}
            subagentMessages={item.subagentMessages}
            isChatLoading={isChatLoading}
            isLastAssistantMessage={itemIndex === lastAssistantGroupIndex}
            isCancelled={isCancelled}
            statusText={statusText}
          />
        );
      })}
    </div>
  );
}
